import { useState } from 'react'
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import { api } from './api'

// Optimized QueryClient configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error: any) => {
        if (error?.status === 400 || error?.status === 401) return false
        return failureCount < 2
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
    },
    mutations: {
      retry: false,
    },
  },
})

function AppContent() {
  const [error] = useState<string>('')

  // Health check
  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ['health'],
    queryFn: api.getHealth,
    retry: 1,
  })

  // Loading screen
  if (healthLoading) {
    return (
      <div className="fixed inset-0 grid place-items-center bg-gradient-to-b from-bg-start to-bg-end z-[80]">
        <div className="text-center space-y-4">
          <div className="text-6xl animate-bounce">🚀</div>
          <div className="text-text-secondary text-lg font-medium">Загрузка приложения...</div>
        </div>
      </div>
    )
  }

  // Error screen
  if (!healthData) {
    return (
      <div className="fixed inset-0 grid place-items-center bg-gradient-to-b from-bg-start to-bg-end z-[80]">
        <div className="text-center space-y-4">
          <div className="text-6xl">❌</div>
          <div className="text-xl font-semibold text-white">Ошибка загрузки</div>
          <div className="text-text-secondary">Не удалось подключиться к серверу</div>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-secondary hover:bg-secondary-hover text-secondary-foreground font-medium rounded-lg transition-colors min-h-[44px] min-w-[44px]"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="app-layout min-w-[320px]">
      <div className="game-area">
        <div className="game-field-container relative">
          <div className="text-center space-y-4 p-8">
            <div className="text-6xl">🎉</div>
            <h1 className="text-2xl font-bold text-white">Приложение готово!</h1>
            <p className="text-text-secondary">
              Сервер работает: {healthData.ok ? '✅' : '❌'}
            </p>
            {healthData.version && (
              <p className="text-text-muted">Версия: {healthData.version}</p>
            )}
          </div>
        </div>
      </div>

      {/* Общие ошибки */}
      {error && (
        <div className="fixed top-4 left-4 right-4 z-50 bg-red-500/90 text-white text-sm px-4 py-2 rounded-lg shadow-lg">
          {error}
        </div>
      )}
    </div>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  )
}